import {useContext} from 'react';

import {useTranslation} from 'react-i18next';

import {Box, Typography} from '@mui/material';

import {culturalAlignmentColors} from '../WorkforceProfileDetail.colors';

import {LanguageContext} from '../../../../../../contexts/LanguageContext/LanguageContext';
import {orangeColors} from '../../../../../../layouts/theme/designSystem';
import {WorkforceProfileCulturalFitTopQuestion} from '../../../types/WorkforceProfileTypes';
import {
  COMPANY_MARKER_COLOR,
  getAlignmentBadgeColors,
  PROFILE_MARKER_COLOR,
  resolveOverlapPercent,
  scaleValueToPercent,
} from '../../../utils/WorkforceProfileDetail/workforceCulturalAlignmentUtils';

export interface Props {
  question: WorkforceProfileCulturalFitTopQuestion;
}

export const WorkforceCulturalAlignmentDimensionCard = ({question}: Props) => {
  const {t} = useTranslation('translation', {
    keyPrefix: 'workforceProfilesPage.detail.culturalFitAnalysis.dimensions',
  });
  const currentLanguage = useContext(LanguageContext);

  const {minValue, maxValue} = question.scaleConfiguration;
  const companyPercent = scaleValueToPercent(question.companyValue, minValue, maxValue);
  const profilePercent = scaleValueToPercent(question.workforceProfileValue, minValue, maxValue);
  const overlapPercent = resolveOverlapPercent(question.overlap);
  const alignmentBadge = getAlignmentBadgeColors(question.overlapLabel);
  const alignmentLabel = t(question.overlapLabel);
  const overlapLeft = Math.min(companyPercent, profilePercent);
  const overlapWidth = Math.abs(companyPercent - profilePercent);
  const scaleSteps = Array.from({length: maxValue - minValue + 1}, (_, index) => minValue + index);
  const description = question.description?.[currentLanguage] ?? question.description?.en ?? null;

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: `0.5px solid ${orangeColors[3]}`,
        borderRadius: '12px',
        px: 2,
        py: 1.75,
      }}
    >
      <Box sx={{display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 0.25}}>
        <Typography variant='body2' sx={{fontWeight: 500, color: 'text.primary', lineHeight: 1.4}}>
          {question.title[currentLanguage]}
        </Typography>
        <Typography
          component='span'
          sx={{
            fontSize: 11,
            px: 1,
            py: 0.25,
            borderRadius: '12px',
            bgcolor: alignmentBadge.bgcolor,
            color: alignmentBadge.color,
            whiteSpace: 'nowrap',
            fontWeight: 500,
            flexShrink: 0,
          }}
        >
          {alignmentLabel} · {overlapPercent}%
        </Typography>
      </Box>

      <Box sx={{position: 'relative', height: 6, bgcolor: orangeColors[5], borderRadius: '3px', mt: 3, mb: 1}}>
        {scaleSteps.map((step) => (
          <Box
            key={step}
            sx={{
              position: 'absolute',
              top: '50%',
              left: `${scaleValueToPercent(step, minValue, maxValue)}%`,
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: culturalAlignmentColors.scaleStepMarker,
              transform: 'translate(-50%, -50%)',
            }}
          />
        ))}
        {overlapWidth > 0 && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: `${overlapLeft}%`,
              width: `${overlapWidth}%`,
              height: '100%',
              borderRadius: '3px',
              bgcolor: culturalAlignmentColors.overlapZone,
              opacity: 0.35,
            }}
          />
        )}
        <Box
          title={`${t('companyTooltip')}: ${question.companyValue}`}
          sx={{
            position: 'absolute',
            top: '50%',
            left: `${companyPercent}%`,
            width: 14,
            height: 14,
            borderRadius: '50%',
            border: '2.5px solid',
            borderColor: 'background.paper',
            bgcolor: COMPANY_MARKER_COLOR,
            transform: 'translate(-50%, -50%)',
            zIndex: 2,
          }}
        />
        <Box
          title={`${t('profileTooltip')}: ${question.workforceProfileValue}`}
          sx={{
            position: 'absolute',
            top: '50%',
            left: `${profilePercent}%`,
            width: 14,
            height: 14,
            borderRadius: '50%',
            border: '2.5px solid',
            borderColor: 'background.paper',
            bgcolor: PROFILE_MARKER_COLOR,
            transform: 'translate(-50%, -50%)',
            zIndex: 2,
          }}
        />
      </Box>

      <Box sx={{display: 'flex', justifyContent: 'space-between', gap: 1, mt: 0.5}}>
        <Typography sx={{fontSize: 12, color: 'text.secondary', flex: 1, lineHeight: 1.4}}>
          {question.scaleConfiguration.labelLeft[currentLanguage]}
        </Typography>
        <Typography sx={{fontSize: 11, color: 'text.disabled', flexShrink: 0, px: 0.5}}>
          {t('scaleRange', {min: minValue, max: maxValue})}
        </Typography>
        <Typography
          sx={{fontSize: 12, color: 'text.secondary', flex: 1, lineHeight: 1.4, textAlign: 'right'}}
        >
          {question.scaleConfiguration.labelRight[currentLanguage]}
        </Typography>
      </Box>

      {description && (
        <Typography variant='caption' color='text.secondary' sx={{display: 'block', mt: 1.25, lineHeight: 1.5}}>
          {description}
        </Typography>
      )}
    </Box>
  );
};
